import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import { Stack, Typography } from '@mui/material';
import { reportMixPanel } from '@55degrees/consent-tracker';
import useAppTheme from '../../../hooks/useAppTheme';
import explainingChartsDark from '../../../assets/images/person-explaining-charts-dark.png';
import explainingChartsLight from '../../../assets/images/person-explaining-charts-light.png';
import { ProductFruitsReporter } from '../../../components/Helpers/ProductFruitsInit';
import { boldItalicText } from '../../../i18n/formattedMessageXMLTags';
import { HeroProps } from '../types';
import { RegisteredMark } from '../style';
import {
  HeroButton,
  HeroButtonContainer,
  HeroHeader,
  HeroBody,
  HeroSection,
  HeroTitle,
  HeroImage,
} from './style';
import DataLoaderContext from '../../../contexts/DataLoaderContext';

export default function Hero({ openDataLoaderDialog, loadExampleData }: HeroProps) {
  const navigate = useNavigate();
  const { setTrigger } = useContext(DataLoaderContext);
  const { isLaptop, isWideScreen, isDarkTheme } = useAppTheme();

  const onClickCreateDataSet = () => {
    setTrigger('Create');
    openDataLoaderDialog('create');
    reportMixPanel('Data Create Start');
    ProductFruitsReporter.track('openCreateDS');
  };

  const onClickLoadExampleData = () => {
    loadExampleData();
    navigate('/example');
  };

  return (
    <HeroSection>
      <HeroHeader>
        <HeroTitle variant="h2">
          <FormattedMessage id="landing.page.hero.title" defaultMessage="Hello!" />
        </HeroTitle>
        <HeroImage
          src={isDarkTheme ? explainingChartsDark : explainingChartsLight}
          alt="Person explaining charts"
          loading="lazy"
          islaptop={isLaptop}
          iswidescreen={isWideScreen}
        />
      </HeroHeader>
      <HeroBody>
        <Stack spacing={2}>
          <Typography variant="body1" color="textPrimary">
            <FormattedMessage
              id="landing.page.hero.hook"
              defaultMessage="Every day you turn ideas into outcomes."
            />
          </Typography>
          <Typography variant="body1" color="textPrimary">
            <FormattedMessage
              id="landing.page.hero.body"
              defaultMessage="ActionableAgile{reg} Analytics helps you understand how
              work flows through your system so you can make that journey from 'started' to ‘finished’ as
              swift, smooth, and predictable as possible. Whether you want to learn from your past, manage
              the present, or forecast the future, we have the flow metrics and simulations you’ll need to
              make it happen. Ready to make work flow? Load an existing data set or create a new one. Just
              want to look around? Use example data to explore at your own pace."
              values={{ reg: <RegisteredMark>®</RegisteredMark> }}
            />
          </Typography>
          <Typography variant="body1" color="textPrimary">
            <FormattedMessage
              id="landing.page.hero.personal.data"
              defaultMessage="Ready to make work flow? <b>Load an existing data set</b> or <b>create a new</b> one."
              values={{ ...boldItalicText }}
            />
          </Typography>
        </Stack>

        <Typography variant="body1" color="textPrimary">
          <FormattedMessage
            id="landing.page.hero.example.data"
            defaultMessage="Just want to look around? <b>Use example data</b> to explore at your own pace."
            values={{ ...boldItalicText }}
          />
        </Typography>
      </HeroBody>
      <HeroButtonContainer islaptop={isLaptop}>
        <HeroButton variant="contained" onClick={onClickCreateDataSet}>
          <FormattedMessage
            id="landing.page.button.create.dataset"
            defaultMessage="Create new data set"
          />
        </HeroButton>
        <HeroButton variant="contained" onClick={onClickLoadExampleData}>
          <FormattedMessage
            id="landing.page.button.use.example.data"
            defaultMessage="Use example data"
          />
        </HeroButton>
      </HeroButtonContainer>
    </HeroSection>
  );
}
